import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../assets/css/composer.css';
import {
  createCampaignApi,
  updateCampaignApi,
  scheduleCampaignApi,
  sendCampaignApi,
} from '../api/campaigns.api';
import { getSegmentsApi } from '../api/audience.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';

const SEGMENT_OPTIONS = {
  saved: [],
  all: ['All Users'],
  org: ['ABC Realty Group', 'North Campus Safety', 'Grace Community Church'],
  custom: ['Custom Filter'],
};

const SEND_TIME_LABELS = {
  now: 'Sending now',
  today3: 'Today 3:00 PM',
  tomorrow9: 'Tomorrow 9:00 AM',
  custom: 'Custom time',
};

const CAMPAIGN_TYPES = [
  { value: 'announcement', label: 'Announcement' },
  { value: 'alert', label: 'Alert' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'broadcast', label: 'Broadcast' },
];

/** Resolve a future scheduledAt ISO string from the sendTime selector. */
function resolveScheduledAt(sendTime, customTime) {
  const now = new Date();
  if (sendTime === 'now') return null;
  if (sendTime === 'today3') {
    const d = new Date();
    d.setHours(15, 0, 0, 0);
    // If already past 3 PM push to tomorrow
    if (d <= now) d.setDate(d.getDate() + 1);
    return d.toISOString();
  }
  if (sendTime === 'tomorrow9') {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  if (sendTime === 'custom' && customTime) {
    return new Date(customTime).toISOString();
  }
  return null;
}

export default function MessageComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pre-fill filterDefinition if navigated from Audience Builder / Saved Segments
  const incomingFilter = location.state?.filterDefinition || null;

  const [segmentOptions, setSegmentOptions] = useState([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [campaignName, setCampaignName] = useState('Trusted Circle completion reminder');
  const [campaignType, setCampaignType] = useState('announcement');
  const [audienceSource, setAudienceSource] = useState('saved');
  const [segment, setSegment] = useState('All Users');
  const [channels, setChannels] = useState({ push: true, email: true, sms: false, inapp: false });
  const [sendTime, setSendTime] = useState('today3');
  const [customTime, setCustomTime] = useState('');
  const [msgTitle, setMsgTitle] = useState('Complete your Trusted Circle');
  const [msgBody, setMsgBody] = useState(
    'Add at least 1 trusted contact so Guardian Link can notify the right people during an urgent event.'
  );
  const [ctaEnabled, setCtaEnabled] = useState(true);
  const [ctaLabel, setCtaLabel] = useState('Add contacts');
  const [ctaUrl, setCtaUrl] = useState('app://trusted-circle');
  const [reportingTag, setReportingTag] = useState('');
  const [previewTab, setPreviewTab] = useState('push');
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  // Persisted campaign ID after first save — used for schedule/send
  const [savedCampaignId, setSavedCampaignId] = useState(null);

  // Load saved segments for the dropdown
  useEffect(() => {
    apiHandler(() => getSegmentsApi({ page: 1, limit: 100, type: 'all' })).then((r) => {
      if (r.success) {
        const rows = unwrapData(r)?.segments || [];
        const opts = rows.map((s) => ({ id: s.id, name: s.segmentName }));
        setSegmentOptions(opts);
        if (opts[0]) setSelectedSegmentId(opts[0].id);
      }
    });
  }, []);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }

  function buildChannelArray() {
    const ch = [];
    if (channels.push) ch.push('push');
    if (channels.email) ch.push('email');
    if (channels.sms) ch.push('sms');
    if (channels.inapp) ch.push('in_app');
    return ch.length ? ch : ['push'];
  }

  function buildCampaignBody(overrides = {}) {
    return {
      campaignName: campaignName.trim(),
      campaignType,
      segmentId: audienceSource === 'saved' ? (selectedSegmentId || undefined) : undefined,
      channel: buildChannelArray(),
      messageTitle: msgTitle.trim(),
      messageBody: msgBody.trim(),
      ctaLabel: ctaEnabled ? ctaLabel.trim() : undefined,
      ctaUrl: ctaEnabled ? ctaUrl.trim() : undefined,
      reportingTag: reportingTag.trim() || undefined,
      ...overrides,
    };
  }

  /** Create or update campaign. Returns the campaign ID on success. */
  async function persistCampaign() {
    const body = buildCampaignBody();
    let result;
    if (savedCampaignId) {
      result = await apiHandler(() => updateCampaignApi(savedCampaignId, body));
    } else {
      result = await apiHandler(() => createCampaignApi(body));
    }
    if (result.success) {
      const data = unwrapData(result);
      const id = data?.id || data?.data?.id || savedCampaignId;
      setSavedCampaignId(id);
      return id;
    }
    return null;
  }

  async function saveDraft() {
    if (!campaignName.trim()) { showToast('Campaign name is required', 'error'); return; }
    setSaving(true);
    const id = await persistCampaign();
    setSaving(false);
    if (id) {
      showToast('Draft saved!', 'success');
      setTimeout(() => navigate('/campaign-manager'), 1200);
    } else {
      showToast('Failed to save draft', 'error');
    }
  }

  /** Called from the Schedule modal confirm button */
  async function handleScheduleOrSend() {
    if (!campaignName.trim()) { showToast('Campaign name is required', 'error'); return; }
    setSaving(true);

    // Step 1: persist campaign (create if not yet saved)
    const id = await persistCampaign();
    if (!id) {
      setSaving(false);
      showToast('Failed to save campaign', 'error');
      return;
    }

    const scheduledAt = resolveScheduledAt(sendTime, customTime);

    if (sendTime === 'now') {
      // Send immediately
      const sendResult = await apiHandler(() => sendCampaignApi(id));
      setSaving(false);
      setShowModal(false);
      if (sendResult.success) {
        const data = unwrapData(sendResult);
        const recipients = data?.recipientCount ?? 0;
        showToast(`Campaign sent! ${recipients} recipient${recipients !== 1 ? 's' : ''} reached.`, 'success');
        setTimeout(() => navigate('/campaign-manager'), 1500);
      } else {
        showToast(sendResult.message || 'Send failed', 'error');
      }
    } else {
      // Schedule for later
      if (!scheduledAt) {
        setSaving(false);
        showToast('Please select a valid future time', 'error');
        return;
      }
      const schedResult = await apiHandler(() => scheduleCampaignApi(id, { scheduledAt }));
      setSaving(false);
      setShowModal(false);
      if (schedResult.success) {
        showToast('Campaign scheduled successfully! 🎉', 'success');
        setTimeout(() => navigate('/campaign-manager'), 1500);
      } else {
        showToast(schedResult.message || 'Schedule failed', 'error');
      }
    }
  }

  function toggleChannel(ch) {
    setChannels((prev) => ({ ...prev, [ch]: !prev[ch] }));
  }

  const charCount = msgBody.length;
  const channelLabels = Object.entries(channels)
    .filter(([, v]) => v)
    .map(([k]) => ({ push: 'Push', email: 'Email', sms: 'SMS', inapp: 'In-App' }[k]))
    .join(' + ');

  const selectedSegmentName =
    audienceSource === 'saved'
      ? segmentOptions.find((s) => s.id === selectedSegmentId)?.name || '—'
      : segment;

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed', top: 70, right: 20, zIndex: 9999,
            background: toast.type === 'success' ? '#F0FDF4' : toast.type === 'error' ? '#FEF2F2' : '#EFF6FF',
            border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : toast.type === 'error' ? '#FECACA' : '#BFDBFE'}`,
            color: toast.type === 'success' ? '#15803D' : toast.type === 'error' ? '#B91C1C' : '#1D4ED8',
            padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          }}
        >
          <i
            className={`fas ${toast.type === 'success' ? 'fa-circle-check' : toast.type === 'error' ? 'fa-circle-xmark' : 'fa-circle-info'}`}
            style={{ marginRight: 7 }}
          />
          {toast.msg}
        </div>
      )}

      <PageHeader title="Message Composer" icon="fa-pen-nib" trail={[{ label: 'Message Composer' }]} />

      {/* 3-Column Grid */}
      <div className="composer-grid">

        {/* LEFT: Campaign Form */}
        <div className="composer-col-left fade-up delay-2">

          {/* Campaign Details */}
          <div className="section-card mb-4">
            <div className="section-header">
              <div className="section-title">
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-flag" style={{ fontSize: 12, color: '#ec7426' }} />
                </div>
                Campaign Details
              </div>
              {savedCampaignId && (
                <span style={{ fontSize: 11, color: '#64748B', background: '#F1F5F9', padding: '3px 8px', borderRadius: 6 }}>
                  <i className="fas fa-floppy-disk" style={{ marginRight: 4 }} />
                  Saved
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Campaign Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="comp-input"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g. Trusted Circle completion reminder"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Campaign Type</label>
                  <div className="select-wrap">
                    <select className="comp-select" value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
                      {CAMPAIGN_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down select-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Reporting Tag</label>
                  <input
                    type="text"
                    className="comp-input"
                    value={reportingTag}
                    onChange={(e) => setReportingTag(e.target.value)}
                    placeholder="e.g. nurture-tc"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Audience Source <span className="required">*</span></label>
                  <div className="select-wrap">
                    <select
                      className="comp-select"
                      value={audienceSource}
                      onChange={(e) => {
                        setAudienceSource(e.target.value);
                        if (e.target.value !== 'saved') setSegment(SEGMENT_OPTIONS[e.target.value]?.[0] || '');
                      }}
                    >
                      <option value="saved">Saved Segment</option>
                      <option value="all">All Users</option>
                      <option value="org">Organization</option>
                      <option value="custom">Custom Filter</option>
                    </select>
                    <i className="fas fa-chevron-down select-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Segment <span className="required">*</span></label>
                  <div className="select-wrap">
                    <select
                      className="comp-select"
                      value={audienceSource === 'saved' ? (selectedSegmentId || '') : segment}
                      onChange={(e) => {
                        if (audienceSource === 'saved') {
                          setSelectedSegmentId(e.target.value || '');
                        } else {
                          setSegment(e.target.value);
                        }
                      }}
                    >
                      {audienceSource === 'saved'
                        ? segmentOptions.length === 0
                          ? <option value="">No segments yet</option>
                          : segmentOptions.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))
                        : (SEGMENT_OPTIONS[audienceSource] || []).map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                    </select>
                    <i className="fas fa-chevron-down select-icon" />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Channel <span className="required">*</span></label>
                  <div className="channel-picker">
                    {[
                      ['push', 'fa-bell', 'Push'],
                      ['email', 'fa-envelope', 'Email'],
                      ['sms', 'fa-comment-sms', 'SMS'],
                      ['inapp', 'fa-mobile-screen', 'In-App'],
                    ].map(([key, icon, label]) => (
                      <label key={key} className={`channel-opt${channels[key] ? ' selected' : ''}`} onClick={() => toggleChannel(key)}>
                        <i className={`fas ${icon}`} /> {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Send Time <span className="required">*</span></label>
                  <div className="select-wrap">
                    <select className="comp-select" value={sendTime} onChange={(e) => setSendTime(e.target.value)}>
                      <option value="now">Send Now</option>
                      <option value="today3">Today 3:00 PM</option>
                      <option value="tomorrow9">Tomorrow 9:00 AM</option>
                      <option value="custom">Custom…</option>
                    </select>
                    <i className="fas fa-chevron-down select-icon" />
                  </div>
                  {sendTime === 'custom' && (
                    <input
                      type="datetime-local"
                      className="comp-input mt-2"
                      value={customTime}
                      onChange={(e) => setCustomTime(e.target.value)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Message Content */}
          <div className="section-card mb-4">
            <div className="section-header">
              <div className="section-title">
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-pen-nib" style={{ fontSize: 12, color: '#074f78' }} />
                </div>
                Message Content
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="comp-tag" style={charCount > 160 ? { color: '#C62828' } : {}}>{`Body: ${charCount} / 160`}</span>
              </div>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Message Title</label>
                <input
                  type="text"
                  className="comp-input"
                  value={msgTitle}
                  onChange={(e) => setMsgTitle(e.target.value)}
                  placeholder="e.g. Complete your Trusted Circle"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Message Body <span className="required">*</span></label>
                {/* <div className="ygif-banner">
                  <i className="fas fa-wand-magic-sparkles" />
                  <span>YGIF Editor active — AI-assisted copy suggestions enabled</span>
                  <button className="ygif-toggle" onClick={() => showToast('YGIF Editor configuration opened', 'info')}>Configure</button>
                </div> */}
                <textarea
                  className="comp-textarea"
                  rows={3}
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  placeholder="Write your message here…"
                />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-arrow-pointer" style={{ fontSize: 12, color: '#2E7D32' }} />
                </div>
                Call to Action
              </div>
              <div className="toggle-wrap">
                <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Enable CTA</span>
                <div className={`toggle${ctaEnabled ? ' active' : ''}`} onClick={() => setCtaEnabled((p) => !p)}>
                  <div className="toggle-knob" />
                </div>
              </div>
            </div>
            {ctaEnabled && (
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Button Label</label>
                    <input
                      type="text"
                      className="comp-input"
                      value={ctaLabel}
                      onChange={(e) => setCtaLabel(e.target.value)}
                      placeholder="e.g. Add contacts"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">CTA URL / Deep Link</label>
                    <input
                      type="text"
                      className="comp-input"
                      value={ctaUrl}
                      onChange={(e) => setCtaUrl(e.target.value)}
                      placeholder="app://trusted-circle"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="composer-actions">
            <button
              type="button"
              className="action-btn"
              style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
              onClick={saveDraft}
              disabled={saving}
            >
              <i className="fas fa-floppy-disk" style={{ fontSize: 12 }} />
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              className="action-btn btn-orange"
              onClick={() => setShowModal(true)}
              disabled={saving}
            >
              <i className="fas fa-calendar-check" style={{ fontSize: 12 }} />
              {sendTime === 'now' ? 'Send Now' : 'Schedule'}
            </button>
          </div>
        </div>

        {/* MIDDLE: Settings */}
        {/* <div className="composer-col-mid fade-up delay-3">
          <div className="section-card" style={{ position: 'sticky', top: 80 }}>
            <div className="section-header">
              <div className="section-title">
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-sliders" style={{ fontSize: 12, color: '#64748B' }} />
                </div>
                Settings
              </div>
            </div>
            <div className="card-body">
              {[
                { key: 'quietHours', icon: 'fa-moon', color: '#074f78', label: 'Use quiet hours', desc: 'Avoid sending 10 PM – 8 AM local time' },
                { key: 'suppressRecent', icon: 'fa-ban', color: '#C62828', label: 'Suppress recent sends', desc: 'Skip users messaged in last 24 hrs' },
                { key: 'excludeOptedOut', icon: 'fa-user-slash', color: '#D9A441', label: 'Exclude opted-out users', desc: 'Honor unsubscribe preferences' },
                { key: 'trackOpen', icon: 'fa-eye', color: '#2E7D32', label: 'Track open events', desc: 'Log when notification is viewed' },
                { key: 'requireApproval', icon: 'fa-shield-check', color: '#ec7426', label: 'Require approval', desc: 'Send to supervisor before delivery' },
              ].map(s => (
                <div key={s.key} className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label"><i className={`fas ${s.icon}`} style={{ color: s.color }} /> {s.label}</div>
                    <div className="setting-desc">{s.desc}</div>
                  </div>
                  <div className={`toggle${settings[s.key] ? ' active' : ''}`} onClick={() => toggleSetting(s.key)}><div className="toggle-knob" /></div>
                </div>
              ))} */}

        {/* Audience Estimate */}
        {/* <div className="audience-estimate">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>Estimated Reach</span>
                  <span className="comp-tag" style={{ background: '#EFF6FF', color: '#074f78' }}>Updated live</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 6 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: '#102A43', letterSpacing: -1, fontFamily: "'Roboto Slab',serif" }}>4,218</span>
                  <span style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>users</span>
                </div>
                <div className="reach-bar-wrap"><div className="reach-bar" style={{ width: '73%' }} /></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: '#94A3B8' }}>
                  <span>of 5,790 in segment</span>
                  <span style={{ color: '#2E7D32', fontWeight: 600 }}>73%</span>
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {channels.push && <div className="reach-chip"><i className="fas fa-bell" /> 3,102 push</div>}
                  {channels.email && <div className="reach-chip"><i className="fas fa-envelope" /> 2,844 email</div>}
                  {channels.sms && <div className="reach-chip"><i className="fas fa-comment-sms" /> 1,240 sms</div>}
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* RIGHT: Preview */}
        {/* <div className="composer-col-right fade-up delay-4">
          <div style={{ position: 'sticky', top: 80 }}>
            <div className="section-card">
              <div className="section-header">
                <div className="section-title">
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-eye" style={{ fontSize: 12, color: '#64748B' }} />
                  </div>
                  Preview
                </div>
                <div className="preview-tabs">
                  <button className={`ptab${previewTab === 'push' ? ' active' : ''}`} onClick={() => setPreviewTab('push')}>Push</button>
                  <button className={`ptab${previewTab === 'email' ? ' active' : ''}`} onClick={() => setPreviewTab('email')}>Email</button>
                </div>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 20px' }}>
                <div className="phone-mockup">
                  <div className="phone-notch" />
                  <div className="phone-screen">
                    <div className="phone-statusbar">
                      <span>9:41</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <i className="fas fa-signal" style={{ fontSize: 8 }} />
                        <i className="fas fa-wifi" style={{ fontSize: 8 }} />
                        <i className="fas fa-battery-three-quarters" style={{ fontSize: 8 }} />
                      </div>
                    </div>

                    {previewTab === 'push' && (
                      <div className="notif-card">
                        <div className="notif-header">
                          <div className="notif-app-icon"><i className="fas fa-shield-heart" style={{ fontSize: 10, color: '#fff' }} /></div>
                          <span className="notif-app-name">Guardian Link</span>
                          <span className="notif-time">now</span>
                        </div>
                        <div className="notif-title">{msgTitle || 'Message Title'}</div>
                        <div className="notif-body">{msgBody || 'Message body will appear here…'}</div>
                        {ctaEnabled && <div className="notif-cta">{ctaLabel || 'Action'}</div>}
                      </div>
                    )}

                    {previewTab === 'email' && (
                      <div className="email-preview">
                        <div className="email-topbar">
                          <div className="email-from">
                            <div className="email-avatar">GL</div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#0F172A' }}>Guardian Link</div>
                              <div style={{ fontSize: 9, color: '#94A3B8' }}>no-reply@guardianlink.io</div>
                            </div>
                          </div>
                        </div>
                        <div style={{ padding: '14px 12px' }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: '#102A43', fontFamily: "'Roboto Slab',serif", marginBottom: 8 }}>{msgTitle || 'Message Title'}</div>
                          <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6, marginBottom: 14 }}>{msgBody || 'Message body…'}</div>
                          {ctaEnabled && (
                            <button style={{ background: 'linear-gradient(135deg,#2E7D32,#3a9e3f)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', width: '100%' }}>
                              {ctaLabel || 'Action'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="phone-wallpaper" />
                  </div>
                </div>

                <div className="preview-meta">
                  <div className="meta-row"><span className="meta-label">Type</span><span className="meta-val">{CAMPAIGN_TYPES.find((t) => t.value === campaignType)?.label}</span></div>
                  <div className="meta-row"><span className="meta-label">Channel</span><span className="meta-val">{channelLabels || '—'}</span></div>
                  <div className="meta-row"><span className="meta-label">Send time</span><span className="meta-val">{SEND_TIME_LABELS[sendTime]}</span></div>
                  <div className="meta-row"><span className="meta-label">Segment</span><span className="meta-val">{selectedSegmentName}</span></div>
                  {reportingTag && (
                    <div className="meta-row"><span className="meta-label">Tag</span><span className="meta-val">{reportingTag}</span></div>
                  )}
                </div>
              </div>
            </div>

            <div className="section-card mt-4">
              <div className="section-header">
                <div className="section-title" style={{ fontSize: 13 }}>
                  <i className="fas fa-clock-rotate-left" style={{ fontSize: 12, color: '#94A3B8' }} />
                  Recent Campaigns
                </div>
              </div>
              <div style={{ padding: '4px 0' }}>
                {[
                  { icon: 'fa-check', bg: '#DCFCE7', color: '#15803D', name: 'Emergency drill reminder', meta: 'Sent · May 5 · 6,102 reached' },
                  { icon: 'fa-calendar', bg: '#DBEAFE', color: '#1D4ED8', name: 'New feature: Safe Zones', meta: 'Scheduled · May 10 · 9:00 AM' },
                  { icon: 'fa-pen', bg: '#F1F5F9', color: '#64748B', name: 'Onboarding series #3', meta: 'Draft · Last edited May 7' },
                ].map((h, i) => (
                  <div key={i} className="hist-item">
                    <div className="hist-dot" style={{ background: h.bg, color: h.color }}><i className={`fas ${h.icon}`} style={{ fontSize: 9 }} /></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>{h.name}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{h.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div> */}

      </div>

      {/* Schedule / Send Confirmation Modal */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-title">
                <i className="fas fa-calendar-check" style={{ color: '#ec7426' }} />
                {sendTime === 'now' ? ' Send Campaign Now' : ' Schedule Campaign'}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Campaign: <strong>{campaignName || '(Untitled)'}</strong></label>
              </div>
              <div className="modal-confirm-box">
                <div className="modal-confirm-row">
                  <i className="fas fa-tag" style={{ color: '#ec7426' }} />
                  <span>Type: {CAMPAIGN_TYPES.find((t) => t.value === campaignType)?.label}</span>
                </div>
                <div className="modal-confirm-row">
                  <i className="fas fa-bookmark" style={{ color: '#074f78' }} />
                  <span>Segment: {selectedSegmentName}</span>
                </div>
                <div className="modal-confirm-row">
                  <i className="fas fa-clock" style={{ color: '#ec7426' }} />
                  <span>Delivery: {sendTime === 'now' ? 'Immediately' : SEND_TIME_LABELS[sendTime]} local</span>
                </div>
                <div className="modal-confirm-row">
                  <i className="fas fa-bell" style={{ color: '#2E7D32' }} />
                  <span>Channels: {channelLabels || '—'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="action-btn"
                style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="action-btn btn-orange"
                onClick={handleScheduleOrSend}
                disabled={saving}
              >
                <i className="fas fa-paper-plane" style={{ fontSize: 12 }} />
                {saving ? 'Processing…' : sendTime === 'now' ? 'Confirm & Send' : 'Confirm & Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
